import { Box, Grid2 } from "@mui/material";
import { isToday, isSameMonth, isSameYear } from "date-fns";
import { useMemo } from "react";

import { CalculatedEnergy, EnergyType } from "api";
import { useFiltersContext } from "components/contexts";
import { Widget, WidgetContent, WidgetProps } from "components/elements/mui";
import { useFormatMessage } from "lang";
import { stalePulseSx } from "styles/animations";
import { formatEnergy } from "utils/energy";

const isDataCurrentForPeriod = (
  dateType: string,
  dataDate: string | undefined,
): boolean => {
  if (!dataDate) return false;
  switch (dateType) {
    case "day":
      return isToday(dataDate);
    case "month":
      return isSameMonth(new Date(), dataDate);
    case "year":
      return isSameYear(new Date(), dataDate);
    case "total":
      return true;
    default:
      return false;
  }
};

export const EnergyInfoSummary = ({
  energyData,
  offline,
  dataDate,
  ...props
}: Partial<WidgetProps> & {
  energyData: { [K in EnergyType]: CalculatedEnergy | undefined };
  offline?: boolean;
  dataDate?: string;
}) => {
  const formatMessage = useFormatMessage();
  const { dateType, energyType: energyTypeContext } = useFiltersContext();

  const energyType = useMemo(() => energyTypeContext[0], [energyTypeContext]);

  return (
    <Widget
      title={
        props.title ??
        `${formatMessage("statistics")} (${formatMessage(energyType).toLowerCase()})`
      }
      icon={props.icon ?? "analytics"}
      size={{ xs: 12 }}
      {...props}
      content={
        <Grid2 container spacing={2}>
          {dateType.map((dateType) => {
            if (energyData[energyType]?.[dateType] == null) return null;
            const formattedEnergy = formatEnergy(
              energyData[energyType][dateType],
            );

            const isCurrent = isDataCurrentForPeriod(dateType, dataDate);
            const showValue = !offline || isCurrent;

            const content = (
              <WidgetContent
                value={showValue ? formattedEnergy.value : "—"}
                unit={showValue ? formattedEnergy.unit : ""}
              />
            );

            return (
              <Widget
                key={dateType}
                title={formatMessage(`widget.this.${dateType}`)}
                size={{
                  xs: 6,
                }}
                content={
                  offline && isCurrent ? (
                    <Box sx={stalePulseSx()}>{content}</Box>
                  ) : (
                    content
                  )
                }
              />
            );
          })}
        </Grid2>
      }
    />
  );
};
